import React from "react";
// UI Framework
import { toast } from "react-toastify";
// Common Components
import {
  TextInput,
  Button,
  DynamicMultiSelectDropdown,
  DynamicDropdown,
  StaticMultiSelectDropdown,
  CheckBox,
} from "src/Components";
// Requests
import { getBaseInstruments } from "src/Requests/Order/tradeTableRequests";
import {
  getDeltaHedgeAccountsForPortfoDropdown,
  getFilterSetting,
  setFilterSetting,
} from "src/Requests/deltaHedgeAccounts";
import { getGroups } from "src/Requests/optionDomain";
import { getStepInstruments } from "src/Requests/stepRequests";
// Utilities
import * as R from "ramda";
import { packetErrorHandler } from "src/Utils/requestUtils";
// Types
import { PositionType } from "src/Interfaces/optionDomain";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { IPortfoPairPositionFilterSetting } from "src/Interfaces/deltaHedgeAccounts";

const positionTypeOptions: DropdownItem[] = [
  { id: 0, name: "همه" },
  { id: PositionType.CS, name: "CS" },
  { id: PositionType.LS, name: "LS" },
  { id: PositionType.PS, name: "PS" },
  { id: PositionType.SS, name: "SS" },
  { id: PositionType.CC, name: "CC" },
  { id: PositionType.PP, name: "PP" },
];

interface IProps {
  onClose: () => void;
  onApplied?: () => void;
}
const FilterSettingModal: React.FC<IProps> = (props) => {
  // local store
  const [searchParam, setSearchParam] =
    React.useState<IPortfoPairPositionFilterSetting>(
      {} as IPortfoPairPositionFilterSetting,
    );

  React.useEffect(() => {
    getFilterSetting()
      .then((res) => {
        setSearchParam(res);
      })
      .catch(packetErrorHandler);
  }, []);

  const handleSearch = () => {
    const cleanObject = R.pickBy(
      (v) => !(v === null || v === "" || (Array.isArray(v) && v.length === 0)),
      searchParam,
    );
    setFilterSetting(cleanObject as IPortfoPairPositionFilterSetting)
      .then((resp) => {
        toast.success(resp.msg);
        if (props.onApplied) props.onApplied();
      })
      .catch(packetErrorHandler)
      .finally(() => {
        props.onClose();
      });
  };

  const searchParamChangeHandler = (name: string, value: any) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };

  return (
    <div className="border border-gray-300 rounded p-3 flex flex-col gap-3 !w-full items-center max-h-[80vh] min-h-0 overflow-hidden">
      <div className="grid grid-cols-2 gap-3 !w-full !min-w-[330px] flex-1 min-h-0 overflow-y-auto pr-1">
        <DynamicMultiSelectDropdown
          dropdown={{
            placeholder: "کاربر ها",
            name: "userIds",
            extraLabel: true,
          }}
          value={searchParam.userIds}
          onChange={searchParamChangeHandler}
          allDefaultValue
          getData={getDeltaHedgeAccountsForPortfoDropdown}
        />
        <div />
        <DynamicMultiSelectDropdown
          dropdown={{
            name: "groupId",
            placeholder: "گروه",
            extraLabel: true,
          }}
          getData={getGroups}
          onChange={searchParamChangeHandler}
          value={searchParam.groupId}
        />
        <StaticMultiSelectDropdown
          name="positionType"
          label="نوع پوزیشن"
          extraLabel
          items={positionTypeOptions}
          onChange={searchParamChangeHandler}
          value={searchParam.positionType}
        />
        <CheckBox
          checked={searchParam.sameStrikePrice}
          label="قیمت اعمال یکسان"
          name="sameStrikePrice"
          onChange={searchParamChangeHandler}
        />
        <div />
        <CheckBox
          checked={searchParam.sameKind}
          label="الزام به هم جنس بودن"
          name="sameKind"
          onChange={searchParamChangeHandler}
        />
        <CheckBox
          checked={searchParam.sameDueDate}
          label="الزام به هم رسید بودن"
          name="sameDueDate"
          onChange={searchParamChangeHandler}
        />
        <DynamicDropdown
          dropdown={{
            name: "ins1",
            placeholder: "نماد 1",
            extraLabel: true,
          }}
          getData={getStepInstruments}
          onChange={searchParamChangeHandler}
          value={searchParam.ins1}
        />
        <DynamicDropdown
          dropdown={{
            name: "ins2",
            placeholder: "نماد 2",
            extraLabel: true,
          }}
          getData={getStepInstruments}
          onChange={searchParamChangeHandler}
          value={searchParam.ins2}
        />
        <DynamicDropdown
          dropdown={{
            name: "baseIns1",
            placeholder: "نماد پایه 1",
            extraLabel: true,
          }}
          getData={() => getBaseInstruments()}
          onChange={searchParamChangeHandler}
          value={searchParam.baseIns1}
        />
        <DynamicDropdown
          dropdown={{
            name: "baseIns2",
            placeholder: "نماد پایه 2",
            extraLabel: true,
          }}
          getData={() => getBaseInstruments()}
          onChange={searchParamChangeHandler}
          value={searchParam.baseIns2}
        />
        <TextInput
          label="حداقل % بازده روزانه"
          onChange={searchParamChangeHandler}
          name="minProfitLock"
          value={searchParam.minProfitLock?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label="حداکثر % بازده روزانه"
          onChange={searchParamChangeHandler}
          name="maxProfitLock"
          value={searchParam.maxProfitLock?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label="حداکثر ٪ ریهج"
          onChange={searchParamChangeHandler}
          name="maxReHedgeRatio"
          value={searchParam.maxReHedgeRatio?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <div />
        <TextInput
          label="حداقل روز تا سررسید(اول)"
          onChange={searchParamChangeHandler}
          name="lessMinRemainingDay"
          value={searchParam.lessMinRemainingDay?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label="حداکثر روز تا سررسید(اول)"
          onChange={searchParamChangeHandler}
          name="lessMaxRemainingDay"
          value={searchParam.lessMaxRemainingDay?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label="حداقل روز تا سررسید(دوم)"
          onChange={searchParamChangeHandler}
          name="moreMinRemainingDay"
          value={searchParam.moreMinRemainingDay?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label="حداکثر روز تا سررسید(دوم)"
          onChange={searchParamChangeHandler}
          name="moreMaxRemainingDay"
          value={searchParam.moreMaxRemainingDay?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label="حداقل ضریب هج"
          onChange={searchParamChangeHandler}
          name="minHedgeFactor"
          value={searchParam.minHedgeFactor?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          label="حداکثر ضریب هج"
          onChange={searchParamChangeHandler}
          name="maxHedgeFactor"
          value={searchParam.maxHedgeFactor?.toString()}
          extraLabel
          kind="negativeDecimalNumbers"
        />
        <TextInput
          name="minYesterdayValue"
          label="حداقل ارزش معاملاتی روز قبل"
          extraLabel
          onChange={searchParamChangeHandler}
          value={searchParam?.minYesterdayValue?.toString() || ""}
          kind="price"
        />

        <TextInput
          name="minLastTradeValue"
          label="حداقل حجم معاملات روز قبل"
          extraLabel
          onChange={searchParamChangeHandler}
          value={searchParam?.minLastTradeValue?.toString() || ""}
          kind="price"
        />
      </div>
      <div className="w-52">
        <Button onClick={handleSearch} text="ثبت" variant="contained" />
      </div>
    </div>
  );
};

export default FilterSettingModal;
